/**
 * TRUY CẬP NỘI DUNG THEO NGÔN NGỮ ĐANG CHỌN.
 *
 * Vì sao tách khỏi `data/*.ts`: dữ liệu gốc là TIẾNG VIỆT (một nguồn sự thật, không nhân đôi),
 * còn bản tiếng Anh tra theo **id** trong `content-en.ts`. Nơi hiển thị gọi các hàm ở đây thay vì
 * đọc thẳng `cls.name`… ⇒ đổi ngôn ngữ là mọi chỗ hiển thị đổi theo, không phải sửa dữ liệu.
 *
 * ⚠️ QUY ƯỚC: `name` lưu trong save (`HeroRecord.name`) vẫn là tiếng Việt — nó là DỮ LIỆU, không
 * phải chữ hiển thị. Chỗ nào in tên nhân vật thì gọi `className(hero.classId)` (tên = tên class).
 */
import { CLASSES, type ClassId } from '../data/classes';
import type { BossAffix } from '../data/enemies';
import { ENEMIES } from '../data/enemies';
import { POTIONS, type PotionKind } from '../data/items';
import type { DifficultyDef, MapDef } from '../data/maps';
import { buildWaves } from '../data/waves';
import {
  BOSS_AFFIX_EN,
  CLASS_EN,
  DIFF_EN,
  ENEMY_EN,
  EXTRA_SKILL_EN,
  MAP_EN,
  POTION_EN,
  SKILL_EN,
  STAGE_BOSS_EN,
  ULT_EN,
  WAVE_TIP_EN,
} from './content-en';
import { getLang } from './index';

/** Hình dạng tối thiểu để tra bản dịch của một skill (chiêu gốc và chiêu ghép dùng chung). */
export interface SkillLike {
  id: string;
  name: string;
  desc: string;
}

const en = (): boolean => getLang() === 'en';

/** Gợi ý wave tiếng Việt lấy từ chính dữ liệu wave (một nguồn sự thật, không chép lại). */
const WAVES_TIP_VI: string[] = buildWaves(0).map((w) => w.tip);

// ------------------------------------------------------------------ class
export function className(id: ClassId): string {
  return (en() && CLASS_EN[id]?.name) || CLASSES[id]?.name || id;
}
export function classTitle(id: ClassId): string {
  return (en() && CLASS_EN[id]?.title) || CLASSES[id]?.title || '';
}
export function classRole(id: ClassId): string {
  return (en() && CLASS_EN[id]?.role) || CLASSES[id]?.role || '';
}
export function classBlurb(id: ClassId): string {
  return (en() && CLASS_EN[id]?.blurb) || CLASSES[id]?.blurb || '';
}

/** Tên class cho mọi class, đã dịch — tiện cho chip/ danh sách. */
export function allClassNames(): Array<{ id: ClassId; name: string }> {
  return Object.keys(CLASSES).map((id) => ({ id: id as ClassId, name: className(id as ClassId) }));
}

// ------------------------------------------------------------------ skill
export function skillName(skill: SkillLike): string {
  const enText = SKILL_EN[skill.id] ?? EXTRA_SKILL_EN[skill.id];
  return (en() && enText?.name) || skill.name;
}
export function skillDesc(skill: SkillLike): string {
  const enText = SKILL_EN[skill.id] ?? EXTRA_SKILL_EN[skill.id];
  return (en() && enText?.desc) || skill.desc;
}

// ------------------------------------------------------------------ màn & độ khó
export function mapName(map: MapDef): string {
  return (en() && MAP_EN[map.id]?.name) || map.name;
}
export function mapBlurb(map: MapDef): string {
  return (en() && MAP_EN[map.id]?.blurb) || map.blurb;
}
export function diffName(def: DifficultyDef): string {
  return (en() && DIFF_EN[def.id]?.name) || def.name;
}
export function diffBlurb(def: DifficultyDef): string {
  return (en() && DIFF_EN[def.id]?.blurb) || def.blurb;
}

// ------------------------------------------------------------------ quái & bình thuốc
export function enemyName(kind: string): string {
  return (en() && ENEMY_EN[kind]) || ENEMIES[kind]?.name || kind;
}
export function bossAffixName(affix: BossAffix): string {
  return (en() && BOSS_AFFIX_EN[affix.id]) || affix.name;
}
export function stageBossName(): string {
  return (en() && STAGE_BOSS_EN) || 'Tàu Hỏa Vương';
}
/** Gợi ý của wave thứ `n` (1..10) — bản tiếng Việt nằm trong `data/waves.ts`. */
export function waveTip(n: number): string {
  const vi = WAVES_TIP_VI[n - 1] ?? '';
  return (en() && WAVE_TIP_EN[n - 1]) || vi;
}

export function potionName(kind: PotionKind): string {
  return (en() && POTION_EN[kind]?.name) || POTIONS[kind].name;
}
/** Tên bình NGẮN cho nút nhỏ: bỏ chữ "Bình "/" Potion". */
export function potionShortName(kind: PotionKind): string {
  return potionName(kind).replace(/^Bình\s+/, '').replace(/\s+Potion$/, '');
}
export function potionDesc(kind: PotionKind): string {
  return (en() && POTION_EN[kind]?.desc) || POTIONS[kind].desc;
}

/**
 * ULTIMATE — tra bản dịch theo `id` (cùng quy ước với skill/ class: nội dung tra theo ID, không
 * đọc thẳng `content-en` từ UI).
 *
 * Người dùng: *"Thêm mỗi class 1 skill chỉ người dùng điều khiển, countdown 3 phút"* — 5 ultimate
 * nằm ở `data/ultimates.ts`, bản dịch ở `ULT_EN`.
 */
export function ultName(u: { id: string; name: string }): string {
  return (en() && ULT_EN[u.id]?.name) || u.name;
}
export function ultDesc(u: { id: string; name: string; desc: string }): string {
  return (en() && ULT_EN[u.id]?.desc) || u.desc;
}
